import { useState, useCallback, useEffect } from 'react';
import { ActionState } from '@/types/actions';
import bookingService from '@/services/bookingService';
import { Booking } from '@/types/calendar';

interface CalendarState {
  bookings: Booking[];
  isLoading: boolean;
  isUpdating: boolean;
  error: string | null;
}

const initialState: CalendarState = {
  bookings: [],
  isLoading: false,
  isUpdating: false,
  error: null
};

export function useCalendarData(userId?: string) {
  const [state, setState] = useState<CalendarState>(initialState);

  useEffect(() => {
    if (!userId) return;
    
    setState(prev => ({ ...prev, isLoading: true }));
    
    bookingService.getUserBookings(userId)
      .then(bookings => {
        setState({
          bookings,
          isLoading: false,
          isUpdating: false,
          error: null
        });
      })
      .catch(error => {
        console.error('useCalendarData: Error fetching bookings:', error);
        setState(prev => ({
          ...prev,
          isLoading: false,
          error: error instanceof Error ? error.message : 'An unknown error occurred'
        }));
      });
  }, [userId]);

  const calendarAction = async (formData: FormData) => {
    const action = formData.get('action') as string;
    setState(prev => ({ ...prev, isUpdating: true, error: null }));

    try {
      switch (action) {
        case 'delete': {
          const bookingId = formData.get('bookingId') as string;
          if (!userId) throw new Error('User ID is required');
          await bookingService.deleteBooking(userId, bookingId);
          break;
        }
        case 'add': {
          if (!userId) throw new Error('User ID is required');
          const booking = {
            talentId: userId,
            eventDate: formData.get('eventDate') as string,
            talentCallTime: formData.get('talentCallTime') as string,
            showStartTime: formData.get('showStartTime') as string,
            eventName: formData.get('eventName') as string,
            location: formData.get('location') as string,
            ...(formData.get('venueName') && { 
              venueName: formData.get('venueName') as string 
            }),
            notes: formData.get('notes') as string || undefined,
            status: 'confirmed' as const,
          };
          await bookingService.addBooking(booking);
          
          const updatedBookings = await bookingService.getUserBookings(userId);
          setState(prev => ({
            ...prev,
            bookings: updatedBookings,
            isUpdating: false,
            error: null
          }));
          break;
        }
        case 'refresh': {
          if (!userId) throw new Error('User ID is required');
          break;
        }
        default: {
          throw new Error(`Unknown action: ${action}`);
        }
      }

      // Fetch updated data
      const bookings = await bookingService.getUserBookings(userId!);
      
      setState({
        bookings,
        isLoading: false,
        isUpdating: false,
        error: null
      });
    } catch (error) {
      console.error('8. Calendar action error:', error);
      setState(prev => ({ 
        ...prev,
        isUpdating: false,
        error: error instanceof Error ? error.message : 'An unknown error occurred'
      }));
      throw error; // Re-throw to be caught by the component
    }
  };

  const refreshCalendar = useCallback(async () => {
    if (!userId) return;
    
    const formData = new FormData();
    formData.append('action', 'refresh');
    await calendarAction(formData);
  }, [userId]);

  return {
    ...state,
    calendarAction,
    refreshCalendar
  };
}